import { useEffect, useState } from 'react'
import { OrdersAPI } from '../api'
import { useNavigate } from 'react-router-dom'
import { useUser, useClerk } from '@clerk/clerk-react'
import { QuantitySelectionModal } from './QuantitySelectionModal'
import { useCache } from './CacheContext'
import { SafeHtml } from './SafeHtml'

const ComplementsPage = () => {
    const { complements, loadingComplements } = useCache()
    const [selectingId, setSelectingId] = useState<string | null>(null)
    const [quantityModalOpen, setQuantityModalOpen] = useState(false)
    const [selectedItemForQuantity, setSelectedItemForQuantity] = useState<any>(null)

    const navigate = useNavigate()
    const { isSignedIn, user } = useUser()
    const clerk = useClerk()

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const createOrder = async (item: any, quantity: number = 1) => {
        if (!user) return

        setSelectingId(item.id)
        setQuantityModalOpen(false)

        try {
            const res = await OrdersAPI.create(
                user.id,
                item.id,
                {
                    userEmail: user.primaryEmailAddress?.emailAddress || '',
                    userName: user.fullName || user.firstName || 'Usuario',
                    planName: quantity > 1 ? `${item.displayName} (x${quantity})` : item.displayName,
                    planPrice: (item.price * quantity).toString(),
                    planCurrency: item.currency,
                    planPeriod: item.payTypeDisplay || (item.payType === 'ANUAL' ? 'Anual' : 'Mensual')
                },
                undefined,
                'complement',
                quantity
            )
            if (res.success) {
                navigate('/dashboard')
            } else {
                console.error('Error selecting plan:', res.error)
                alert('Hubo un error al seleccionar el plan. Por favor intente nuevamente.')
            }
        } catch (error) {
            console.error('Error selecting plan:', error)
            alert('Hubo un error al seleccionar el plan. Por favor intente nuevamente.')
        } finally {
            setSelectingId(null)
            setSelectedItemForQuantity(null)
        }
    }

    const handleSelectPlan = async (item: any) => {
        if (!isSignedIn) {
            clerk.openSignIn()
            return
        }
        if (!user) return

        if (item.displayName && item.displayName.toLowerCase().includes('zoho')) {
            setSelectedItemForQuantity(item)
            setQuantityModalOpen(true)
            return
        }

        await createOrder(item)
    }

    if (loadingComplements && complements.length === 0) return (
        <div className="min-h-screen pt-32 pb-20 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        </div>
    )

    return (
        <div className="min-h-screen bg-white dark:bg-slate-950 pt-12 pb-20">
            {/* Header / Banner */}
            <div className="w-full text-center text-gray-900 dark:text-white mb-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-3xl md:text-5xl font-bold mb-4">
                        Todos nuestros complementos
                    </h1>
                    <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 leading-relaxed max-w-2xl mx-auto">
                        Potencia tu sitio web con nuestras herramientas y servicios adicionales.
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 gap-y-16">
                    {complements.map((item) => (
                        <div
                            key={item.id}
                            onClick={() => navigate('/complemento/' + encodeURIComponent(item.name))}
                            className="bg-white dark:bg-slate-900 rounded-t-[2rem] rounded-tr-[10rem] rounded-b-lg border border-gray-100 dark:border-slate-800 shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.20)] transition-all cursor-pointer relative overflow-visible flex flex-col group"
                        >
                            {/* Background Icon/Image positioned absolute top-right */}
                            <div className="absolute top-0 right-0 pointer-events-none z-0">
                                {item.mainPhotoUrl ? (
                                    <img src={item.mainPhotoUrl} alt={item.displayName} className="w-[130px] h-[130px] object-contain" />
                                ) : (
                                    <div className="w-[130px] h-[130px] rounded-bl-[4rem] bg-gray-50 flex items-center justify-center text-gray-300 font-bold text-4xl">
                                        {item.displayName?.substring(0, 2).toUpperCase()}
                                    </div>
                                )}
                            </div>

                            <div className="p-8 pb-8 flex-1 flex flex-col relative z-10">
                                <div className="mb-6 pr-28">
                                    <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-2 leading-tight">
                                        {item.displayName}
                                    </h3>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-xl font-bold text-gray-900 dark:text-white">
                                            {item.currency} {item.price}
                                        </span>
                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                            {item.payTypeDisplay || (item.payType === 'ANUAL' ? 'por año' : item.payType === 'MENSUAL' ? 'por mes' : '')}
                                        </span>
                                    </div>
                                </div>

                                {/* Features List */}
                                <div className="flex-1">
                                    {item.description && (
                                        <div className="space-y-3">
                                            <SafeHtml
                                                html={item.description}
                                                mode="rich"
                                                className="text-sm text-gray-600 dark:text-gray-300 [&>ul]:space-y-2 [&>ul>li]:flex [&>ul>li]:gap-2 [&>ul>li]:items-start"
                                            />
                                        </div>
                                    )}
                                </div>

                            </div>

                            {/* Footer Area */}
                            <div className="bg-gray-50 dark:bg-slate-900/50 p-6 border-t border-gray-100 dark:border-slate-700/50 min-h-[80px] flex items-center justify-center">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleSelectPlan(item)
                                    }}
                                    disabled={selectingId === item.id}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {selectingId === item.id ? 'Procesando...' : 'Elegir plan'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <QuantitySelectionModal
                isOpen={quantityModalOpen}
                onClose={() => {
                    setQuantityModalOpen(false)
                    setSelectedItemForQuantity(null)
                }}
                item={selectedItemForQuantity}
                onConfirm={(qty) => createOrder(selectedItemForQuantity, qty)}
            />
        </div>
    )
}

export default ComplementsPage
